import {
  addArgentinaDays,
  addArgentinaMonths,
  formatDateForUrl,
  isValidUrlDate,
  startOfArgentinaDay,
} from "@workspace/shared/date";
import { z } from "zod";
import { simpleFilterOperatorSchema } from "@workspace/ui/lib/filter-operators";

const urlDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, "Fecha inválida")
  .refine(isValidUrlDate, "Fecha inválida");

const dateRangeSchema = z
  .object({
    from: urlDateSchema,
    to: urlDateSchema,
  })
  .refine(({ from, to }) => from <= to, "Rango de fechas inválido");

const amountFilterValueSchema = z.union([
  z.object({ amount: z.number().finite(), rule: z.literal("equal") }),
  z.object({ amount: z.number().finite(), rule: z.literal("greater") }),
  z.object({ amount: z.number().finite(), rule: z.literal("less") }),
  z
    .object({
      maxAmount: z.number().finite(),
      minAmount: z.number().finite(),
      rule: z.literal("between"),
    })
    .refine(
      ({ minAmount, maxAmount }) => minAmount <= maxAmount,
      "Rango de montos inválido"
    ),
]);

export const DEADLINE_DURATION_VALUES = [
  "-P1D",
  "P1D",
  "P3D",
  "P1W",
  "P1M",
  "P3M",
] as const;

export type DeadlineDuration = (typeof DEADLINE_DURATION_VALUES)[number];

const dateAnchorSchema = z.object({
  date: urlDateSchema,
  kind: z.literal("date"),
});

const deadlineAnchorSchema = z.union([
  z.object({
    duration: z.enum(DEADLINE_DURATION_VALUES),
    kind: z.literal("relative"),
  }),
  dateAnchorSchema,
]);

const canonicalClauseSchema = z.union([
  z.object({
    key: z.string().min(1),
    operator: simpleFilterOperatorSchema,
    type: z.literal("historicalDate"),
    value: dateRangeSchema,
  }),
  // "Desde" or "hasta" a past date: only one bound of the range.
  z.object({
    key: z.string().min(1),
    operator: z.enum(["since", "until"]),
    type: z.literal("historicalDate"),
    value: dateAnchorSchema,
  }),
  z.object({
    key: z.string().min(1),
    operator: simpleFilterOperatorSchema,
    type: z.literal("unrestrictedDate"),
    value: dateRangeSchema,
  }),
  z.object({
    key: z.string().min(1),
    operator: z.enum(["since", "until"]),
    type: z.literal("deadlineDate"),
    value: deadlineAnchorSchema,
  }),
  z.object({
    key: z.string().min(1),
    operator: z.literal("between"),
    type: z.literal("deadlineDate"),
    value: dateRangeSchema.extend({ kind: z.literal("range") }),
  }),
  z.object({
    key: z.string().min(1),
    type: z.literal("amount"),
    value: amountFilterValueSchema,
  }),
  z.object({
    key: z.string().min(1),
    operator: simpleFilterOperatorSchema,
    type: z.literal("singleSelect"),
    value: z.string().min(1),
  }),
  z.object({
    key: z.string().min(1),
    operator: simpleFilterOperatorSchema,
    type: z.literal("multiSelect"),
    value: z.array(z.string().min(1)).min(1),
  }),
]);

export const listFilterDocumentSchema = z
  .object({
    clauses: z.array(canonicalClauseSchema),
    version: z.literal(1),
  })
  .refine(
    ({ clauses }) =>
      new Set(clauses.map(({ key }) => key)).size === clauses.length,
    "Cada filtro puede aparecer una sola vez"
  );

export type ListFilterDocument = z.infer<typeof listFilterDocumentSchema>;
export type CanonicalListFilterClause = ListFilterDocument["clauses"][number];
export type CanonicalListFilterType = CanonicalListFilterClause["type"];
export type DeadlineListFilterClause = Extract<
  CanonicalListFilterClause,
  { type: "deadlineDate" }
>;

export const EMPTY_LIST_FILTER_DOCUMENT: ListFilterDocument = {
  clauses: [],
  version: 1,
};

export function parseListFilterDocument(
  input: unknown
): ListFilterDocument | undefined {
  return listFilterDocumentSchema.safeParse(input).data;
}

export function parseListFilterDocumentParam(
  input: string | string[] | null | undefined
): ListFilterDocument | undefined {
  if (typeof input !== "string") {
    return;
  }

  try {
    return parseListFilterDocument(JSON.parse(input));
  } catch {
    return;
  }
}

export function findListFilterClause<Type extends CanonicalListFilterType>(
  document: ListFilterDocument | undefined,
  key: string,
  type: Type
): Extract<CanonicalListFilterClause, { type: Type }> | undefined {
  return document?.clauses.find(
    (clause): clause is Extract<CanonicalListFilterClause, { type: Type }> =>
      clause.key === key && clause.type === type
  );
}

export function removeListFilterClauses(
  document: ListFilterDocument | null | undefined,
  keys: readonly string[]
): ListFilterDocument | null {
  if (!document) {
    return null;
  }

  const removedKeys = new Set(keys);
  return {
    ...document,
    clauses: document.clauses.filter(({ key }) => !removedKeys.has(key)),
  };
}

function resolveDeadlineDuration(
  referenceTime: Date,
  duration: DeadlineDuration
) {
  const today = startOfArgentinaDay(referenceTime);
  switch (duration) {
    case "-P1D":
      return addArgentinaDays(today, -1);
    case "P1D":
      return addArgentinaDays(today, 1);
    case "P3D":
      return addArgentinaDays(today, 3);
    case "P1W":
      return addArgentinaDays(today, 7);
    case "P1M":
      return addArgentinaMonths(today, 1);
    case "P3M":
      return addArgentinaMonths(today, 3);
    default:
      return today;
  }
}

export function resolveDateFilterRange(
  clause: Extract<
    CanonicalListFilterClause,
    { type: "deadlineDate" | "historicalDate" | "unrestrictedDate" }
  >,
  referenceTime: Date
): { from?: string; to?: string } {
  const { value } = clause;
  if ("from" in value) {
    return { from: value.from, to: value.to };
  }

  const anchor =
    value.kind === "relative"
      ? formatDateForUrl(resolveDeadlineDuration(referenceTime, value.duration))
      : value.date;

  return clause.operator === "until" ? { to: anchor } : { from: anchor };
}
